// BuildingViewModal.js
import React, { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import InfoIcon from "@mui/icons-material/Info";
import ViewModalHeader from "../../../../../../components/Modals/ModalControls/ModalHeader";
import ModalAccordion from "../../../../../../components/Modals/ModalAccordion";
import {
  buildingGeneralFields,
  buildingComment,
  buildingAddressFields,
  buildingBillingAddressFields,
  buildingAdditionalFields,
} from "../../config/buildingModalConfig";
import RenderFields from "../../../../../../components/Modals/ModalFields/RenderFields";
import RenderAddressFields from "../../../../../../components/Modals/ModalFields/RenderAddressFields";
import AlertSnackbar from "../../../../../../components/Notifications/AlertSnackbar";
import { selectNavHeight } from "../../../../../../redux/navbar/navHeightSlice";
import {
  setSelectedBuilding,
  selectedBuilding,
  clearSelectedBuilding,
  fetchEnrichedBuildingData,
} from "../../../../../../redux/buildings/selectedBuildingSlice";
import {
  selectCurrentPage,
  selectRowsPerPage,
} from "../../../../../../redux/buildings/buildingsPaginationSlice";
import useDebounce from "../../../../../../hooks/useDebounce";
import useBuildingDelete from "../../hooks/useBuildingDelete";
import BuildingInformationDisplay from "./InformationDisplay";
import LoadingCircle from "../../../../../../components/LoadingCircle";
import BuildingContactTable from "./Contacts/BuildingContactTable";
import { clearSelectedBuildingContact } from "../../../../../../redux/buildings/selectedBuildingContactSlice";
import BuildingDevicesTable from "./Devices/BuildingDevicesTable";
import { clearSelectedBuildingDevices } from "../../../../../../redux/buildings/selectedBuildingDevicesSlice";
import ActionsButton from "./ActionsButton";
import useBuildingUpdate from "../../hooks/useBuildingUpdate";
import InspectionWizardController from "../../../../../setup/components/Building/Actions/Run the Inspection Wizard/InspectionWizardModalNoDropdown";
import RenderFieldsBuildings from "./RenderFieldsBuildings";

const BuildingViewModal = ({
  open,
  handleClose,
  handleOpenAddFromView,
  updateList,
  isEditMode,
  setIsEditMode,
  dropdownLists,
}) => {
  const dispatch = useDispatch();
  const navHeight = useSelector(selectNavHeight);
  const buildings = useSelector((state) => state.buildings.buildings);
  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);
  const buildingData = useSelector(selectedBuilding);

  const {
    formData,
    setFormData,
    validationErrors,
    isSubmitting,
    autosaveStatus,
    setAutosaveStatus,
    handleSave,
    setValidationErrors,
  } = useBuildingUpdate(updateList, { currentPage, rowsPerPage });

  // Local UI state for modal expansion, information pane, billing toggle, etc.
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isBilling, setIsBilling] = useState(false);
  const [newDeviceId, setNewDeviceId] = useState(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [isInspectionsWizardOpen, setIsInspectionsWizardOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInfoClick = () => setIsInfoOpen((prev) => !prev);

  // Snackbar state for user notifications.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Create a debounced version of the form data to reduce API calls during typing.
  const debouncedFormData = useDebounce(formData, 1500);

  // Fetch enriched building data when the component is first loaded with a selected building
  useEffect(() => {
    if (open && formData?.buildingID) {
      setIsLoading(true);
      dispatch(fetchEnrichedBuildingData(formData.buildingID))
        .unwrap()
        .then(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching enriched building data:", error);
          setIsLoading(false);
          setSnackbar({
            open: true,
            message: "Failed to fetch complete building data",
            severity: "error",
          });
        });
    }
  }, [open, formData?.buildingID, dispatch]);

  // Auto-save changes if in edit mode.
  useEffect(() => {
    if (isEditMode) {
      setAutosaveStatus("Saving...");
      handleSave(true).then((success) => {
        if (success) {
          setAutosaveStatus("Saved");
          setTimeout(() => setAutosaveStatus(""), 2000);
        } else {
          setAutosaveStatus("");
        }
      });
    }
  }, [debouncedFormData]);

  // Handler for form field changes.
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Toggle edit mode and clear any existing validation errors.
  const handleToggleEdit = () => {
    setIsEditMode((prev) => !prev);
    setValidationErrors({});
  };

  // On closing the modal, clear the selected building and reset edit mode.
  const handleCloseModal = () => {
    handleClose();
    dispatch(clearSelectedBuilding());
    setIsEditMode(false);
    setIsActionsOpen(false);
  };

  // Building deletion (using your custom delete hook).
  const deleteSelected = useBuildingDelete(formData, updateList, setSnackbar);
  const handleDelete = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Building deleted successfully",
        severity: "success",
      });
      dispatch(clearSelectedBuilding());
      setValidationErrors({});
      setIsEditMode(false);
      handleCloseModal();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to delete building",
        severity: "error",
      });
    }
  };

  // Navigation: previous building.
  const handlePrev = useCallback(() => {
    if (!buildings?.length || !formData || !formData.buildingID) return;
    setIsLoading(true);
    const currentIndex = buildings.findIndex(
      (bld) => bld.buildingID === formData.buildingID
    );
    if (currentIndex === -1) return;
    const prevIndex = (currentIndex - 1 + buildings.length) % buildings.length;
    const prevBuilding = buildings[prevIndex];

    // First set the basic building data to avoid UI delay
    dispatch(setSelectedBuilding(prevBuilding));

    // Then fetch the enriched data
    dispatch(fetchEnrichedBuildingData(prevBuilding.buildingID))
      .unwrap()
      .finally(() => {
        setIsLoading(false);
      });
  }, [buildings, formData, dispatch]);

  // Navigation: next building.
  const handleNext = useCallback(() => {
    if (!buildings?.length || !formData || !formData.buildingID) return;
    setIsLoading(true);
    const currentIndex = buildings.findIndex(
      (bld) => bld.buildingID === formData.buildingID
    );
    if (currentIndex === -1) return;
    const nextIndex = (currentIndex + 1) % buildings.length;
    const nextBuilding = buildings[nextIndex];

    // First set the basic building data to avoid UI delay
    dispatch(setSelectedBuilding(nextBuilding));

    // Then fetch the enriched data
    dispatch(fetchEnrichedBuildingData(nextBuilding.buildingID))
      .unwrap()
      .finally(() => {
        setIsLoading(false);
      });
  }, [buildings, formData, dispatch]);

  // Handlers and state for managing contact forms within the modal.
  const [contactForm, setContactForm] = useState({});
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };
  const [showNewContactForm, setShowNewContactForm] = useState(false);
  const [newEntityMode, setNewEntityMode] = useState({
    active: false,
    type: null,
  });
  const handleOpenNew = (entityType) => {
    if (entityType === "contact") {
      dispatch(clearSelectedBuildingContact());
    }
    setNewEntityMode({ active: true, type: entityType });
    setIsInfoOpen(true);
  };

  // Cancel edit/new mode: clear errors and close extra panels.
  const handleCancelModal = () => {
    setIsEditMode(false);
    setValidationErrors({});
    setIsInfoOpen(false);
    setNewEntityMode({ active: false, type: null });
    dispatch(clearSelectedBuildingContact());
    dispatch(clearSelectedBuildingDevices());
  };

  // If there is no building data yet or loading, display a loading indicator.
  if (!formData || Object.keys(formData).length === 0 || isLoading) {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            width: "1000px", // Use the default non-expanded size
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "hidden",
          },
        }}
        style={{ zIndex: 1400 }}
      >
        <ViewModalHeader
          isExpanded={false}
          setIsExpanded={() => {}}
          handleCloseModal={handleCloseModal}
          isEditMode={false}
          handleEditButtonClick={() => {}}
          handleCancel={handleCancelModal}
          handleDelete={() => {}}
          isSaving={false}
          autosaveStatus={""}
          handleOpenAddModal={() => {}}
          disabled={true}
        />
        <div style={{ width: "90%", margin: "0 auto" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl mb-4">Building</h2>
          </div>
          <hr />
        </div>
        <DialogContent
          style={{
            minHeight: "500px",
            width: "100%",
            margin: "0 auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <LoadingCircle size={60} />
          <p className="mt-4 text-gray-600">Loading building data...</p>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          style: {
            position: "absolute",
            top: navHeight,
            left: "50%",
            transform: "translateX(-50%)",
            margin: 4,
            transition: "width 0.3s ease, height 0.3s ease",
            width: isExpanded ? "90%" : "1000px",
            height: `calc(100vh - ${navHeight + 8}px)`,
            overflow: "auto",
          },
        }}
        style={{ zIndex: 1400 }}
      >
        <ViewModalHeader
          isExpanded={isExpanded}
          setIsExpanded={() => setIsExpanded((prev) => !prev)}
          handleCloseModal={handleCloseModal}
          isEditMode={isEditMode}
          handleEditButtonClick={handleToggleEdit}
          handleCancel={handleCancelModal}
          handleDelete={handleDelete}
          isSaving={isSubmitting || autosaveStatus === "Saving..."}
          autosaveStatus={autosaveStatus}
          handleOpenAddModal={handleOpenAddFromView}
        />
        <div style={{ width: "90%", margin: "0 auto" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-3xl mb-4">Building</h2>
            <div>
              <ActionsButton
                formData={formData}
                isActionsOpen={isActionsOpen}
                setIsActionsOpen={setIsActionsOpen}
                setIsInspectionsWizardOpen={setIsInspectionsWizardOpen}
              />
              <button
                onClick={handleInfoClick}
                className={`p-2 bg-white hover:bg-gray-200 transition ${
                  isInfoOpen ? "text-sss-info" : "text-gray-800"
                }`}
              >
                {isInfoOpen ? <InfoIcon /> : <InfoOutlinedIcon />}
              </button>
            </div>
          </div>
          <hr />
        </div>
        <DialogContent
          style={{
            minHeight: "500px",
            width: "90%",
            margin: "0 auto",
            padding: 0,
            height: "100%",
          }}
          sx={{ p: 0, pr: 2, pt: 2 }}
        >
          <div className="flex h-full transition-all duration-300">
            <div
              className={`transition-all duration-300 ${
                isInfoOpen ? "w-5/12" : "w-full"
              } h-full overflow-y-auto`}
            >
              {validationErrors.form && (
                <div className="text-red-500 mb-4">{validationErrors.form}</div>
              )}
              <ModalAccordion label="General" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFieldsBuildings
                    key={formData?.buildingID}
                    fields={buildingGeneralFields}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                    dropdownLists={dropdownLists}
                  />
                </div>
                <div className="flex flex-col md:flex-row gap-4 w-full py-2">
                  <RenderFields
                    fields={buildingComment}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion label="Address" defaultExpanded>
                <div className="flex mb-4">
                  <button
                    onClick={() => setIsBilling(false)}
                    className={`border p-1 text-sm ${
                      !isBilling ? "bg-teal-100" : "bg-gray-100"
                    }`}
                  >
                    Physical
                  </button>
                  <button
                    onClick={() => setIsBilling(true)}
                    className={`border p-1 text-sm ${
                      isBilling ? "bg-teal-100" : "bg-gray-100"
                    }`}
                  >
                    Billing
                  </button>
                </div>
                {isBilling ? (
                  <RenderAddressFields
                    fields={buildingBillingAddressFields}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                ) : (
                  <RenderAddressFields
                    fields={buildingAddressFields}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                  />
                )}
              </ModalAccordion>
              <ModalAccordion label="Additional" defaultExpanded>
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  <RenderFieldsBuildings
                    key={formData?.buildingID}
                    fields={buildingAdditionalFields}
                    formData={formData}
                    handleChange={handleFieldChange}
                    readOnly={!isEditMode}
                    validationErrors={validationErrors}
                    dropdownLists={dropdownLists}
                  />
                </div>
              </ModalAccordion>
              <ModalAccordion label="Contacts" defaultExpanded>
                <BuildingContactTable
                  contactForm={contactForm}
                  handleContactChange={handleContactChange}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("contact")}
                  setShowNewContactForm={setShowNewContactForm}
                />
              </ModalAccordion>
              <ModalAccordion label="Devices" defaultExpanded>
                <BuildingDevicesTable
                  buildingID={formData?.buildingID}
                  isEditMode={isEditMode}
                  onAddNew={() => handleOpenNew("device")}
                  newDeviceId={newDeviceId}
                  setNewDeviceId={setNewDeviceId}
                />
              </ModalAccordion>
            </div>
            <div
              className={`transition-all duration-300 overflow-hidden ${
                isInfoOpen ? "w-7/12 opacity-100" : "w-0 opacity-0"
              } h-full`}
            >
              <div className="h-full overflow-y-auto mt-4 ml-4">
                <div className="p-4">
                  <BuildingInformationDisplay
                    isEditMode={isEditMode}
                    newMode={newEntityMode.active}
                    newEntityType={newEntityMode.type}
                    onCloseNewMode={() =>
                      setNewEntityMode({ active: false, type: null })
                    }
                    updateList={updateList}
                  />
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
        <div style={{ width: "90%", margin: "0 auto" }}>
          <hr />
        </div>
        <DialogActions
          style={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 1500,
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={handlePrev}
            className="p-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowBackIcon />
          </button>
          <button
            onClick={handleNext}
            className="p-2 mr-2 bg-white hover:bg-gray-200 transition"
          >
            <ArrowForwardIcon />
          </button>
        </DialogActions>
      </Dialog>
      <AlertSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        autoHideDuration={3000}
      />
      {isInspectionsWizardOpen && (
        <InspectionWizardController
          isOpen={isInspectionsWizardOpen}
          building={buildingData}
          onClose={() => setIsInspectionsWizardOpen(false)}
          refreshList={() => {
            // Add any specific refresh logic needed after wizard closes
            setIsInspectionsWizardOpen(false); // Close after refresh logic too
          }}
        />
      )}
    </>
  );
};

BuildingViewModal.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  updateList: PropTypes.func,
  handleOpenAddFromView: PropTypes.func,
  isEditMode: PropTypes.bool,
  setIsEditMode: PropTypes.func,
};

export default BuildingViewModal;
